import { clsx } from 'clsx'
import { useId, useState } from 'react'
import { DAYS, DAY_LABELS, DAY_SHORT, SCHEDULE_PRESETS, TIME_OPTIONS, emptySchedule, openDay, scheduleText, slugify, to12h, type DayKey, type Schedule } from '@sonder/shared'
import { Button } from './Button'
import { Collapsible } from './Collapsible'
import { Field, Input, Select } from './Field'
import { Toggle } from './Toggle'
import { Plus, X } from './icons'

type Props = {
  value: Schedule[]
  onChange: (next: Schedule[]) => void
  /** Client mode hides destructive schedule-level actions behind confirmation copy. */
  readOnly?: boolean
  /** Shows "Copy to…" on the open schedule; the caller picks the other locations. */
  onCopy?: (schedule: Schedule) => void
}

function updateSchedule(list: Schedule[], id: string, fn: (s: Schedule) => Schedule) {
  return list.map((s) => (s.id === id ? fn(s) : s))
}

export function ScheduleBuilder({ value, onChange, readOnly, onCopy }: Props) {
  const [activeId, setActiveId] = useState<string>(value[0]?.id ?? '')
  const active = value.find((s) => s.id === activeId) ?? value[0]
  const tabsId = useId()
  const [presetId, setPresetId] = useState<string>('lunch')
  const [customName, setCustomName] = useState('')
  // Collapsed to a summary of every schedule; a location with no hours yet opens straight into the editor.
  const [open, setOpen] = useState(value.length === 0)

  const addSchedule = () => {
    const custom = customName.trim()
    if (presetId === CUSTOM && !custom) return
    const preset = SCHEDULE_PRESETS.find((p) => p.id === presetId) ?? SCHEDULE_PRESETS[0]
    const [base, label] = presetId === CUSTOM ? [slugify(custom), custom] : [preset.id, preset.label]
    let id: string = base
    let n = 2
    while (value.some((s) => s.id === id)) id = `${base}-${n++}`
    const next = emptySchedule(id, label)
    onChange([...value, next])
    setActiveId(id)
    setCustomName('')
  }
  const addRow = <AddRow presetId={presetId} setPresetId={setPresetId} customName={customName} setCustomName={setCustomName} onAdd={addSchedule} />

  const removeSchedule = (id: string) => {
    const next = value.filter((s) => s.id !== id)
    onChange(next)
    setActiveId(next[0]?.id ?? '')
  }

  if (!active) {
    return (
      <div className="grid gap-3">
        <p className="text-small text-dim">No hours yet.</p>
        {addRow}
      </div>
    )
  }

  const setDay = (day: DayKey, fn: (d: Schedule['days'][DayKey]) => Schedule['days'][DayKey]) =>
    onChange(updateSchedule(value, active.id, (s) => ({ ...s, days: { ...s.days, [day]: fn(s.days[day]) } })))

  const applyMondayToAll = () =>
    onChange(updateSchedule(value, active.id, (s) => ({ ...s, days: Object.fromEntries(DAYS.map((d) => [d, structuredClone(s.days.mon)])) as Schedule['days'] })))

  return (
    <Collapsible
      label="Hours"
      open={open}
      onToggle={() => setOpen((o) => !o)}
      summary={
        <span className="grid min-w-0 flex-1 gap-1.5">
          {value.map((s) => (
            <span key={s.id} className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-4">
              <span className="text-small font-medium text-ink">{s.label}</span>
              <span className="text-small text-charcoal tabular-nums">{scheduleText(s)}</span>
            </span>
          ))}
        </span>
      }
    >
      <div className="grid gap-5">
        {/* Schedule tabs: one row that scrolls sideways on a phone rather than wrapping onto a second line. */}
        <div role="tablist" aria-label="Schedules" className="flex gap-1 overflow-x-auto border-b border-gray-200 [scrollbar-width:none] sm:flex-wrap sm:overflow-visible">
          {value.map((s) => (
            <button
              key={s.id}
              role="tab"
              id={`${tabsId}-${s.id}`}
              aria-selected={s.id === active.id}
              aria-controls={`${tabsId}-panel`}
              type="button"
              onClick={() => setActiveId(s.id)}
              className={clsx(
                '-mb-px min-h-10 shrink-0 whitespace-nowrap border-b-2 px-3 text-small transition-colors',
                s.id === active.id ? 'border-ink font-medium text-ink' : 'border-transparent text-dim hover:text-ink',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div id={`${tabsId}-panel`} role="tabpanel" aria-labelledby={`${tabsId}-${active.id}`} className="grid gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <Field label="Schedule name" className="w-56">
              <Input value={active.label} onChange={(e) => onChange(updateSchedule(value, active.id, (s) => ({ ...s, label: e.target.value })))} />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button variant="link" size="sm" arrow={false} onClick={applyMondayToAll}>
                Apply Monday to every day
              </Button>
              {onCopy && (
                <Button variant="link" size="sm" arrow={false} onClick={() => onCopy(active)}>
                  Copy to…
                </Button>
              )}
              {!readOnly && value.length > 1 && (
                <Button variant="danger-link" size="sm" arrow={false} onClick={() => removeSchedule(active.id)}>
                  Remove schedule
                </Button>
              )}
            </div>
          </div>

          <ol className="grid divide-y divide-gray-200 border-y border-gray-200">
            {DAYS.map((day) => {
              const d = active.days[day]
              return (
                <li key={day} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 py-3 md:grid-cols-[6.5rem_auto_1fr]">
                  <span className="pt-1 text-small font-medium text-ink">
                    <span className="md:hidden">{DAY_LABELS[day]}</span>
                    <span className="hidden md:inline">{DAY_SHORT[day]}</span>
                  </span>
                  <Toggle
                    size="sm"
                    checked={!d.closed}
                    onChange={(open) => setDay(day, (cur) => (open ? (cur.ranges.length ? { ...cur, closed: false } : openDay()) : { ...cur, closed: true }))}
                    label={d.closed ? 'Closed' : 'Open'}
                  />
                  <div className={clsx('col-span-2 grid gap-2 md:col-span-1', d.closed && 'max-md:hidden')}>
                    {d.closed ? (
                      // On a phone the switch beside the day already says Closed.
                      <span className="pt-1 text-small text-dim">Closed</span>
                    ) : (
                      d.ranges.map((r, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2">
                          <TimeSelect label={`${DAY_LABELS[day]} open${d.ranges.length > 1 ? ` (${i + 1})` : ''}`} value={r.open} onChange={(v) => setDay(day, (cur) => ({ ...cur, ranges: cur.ranges.map((x, j) => (j === i ? { ...x, open: v } : x)) }))} />
                          <span className="text-dim" aria-hidden="true">
                            –
                          </span>
                          <TimeSelect label={`${DAY_LABELS[day]} close${d.ranges.length > 1 ? ` (${i + 1})` : ''}`} value={r.close} onChange={(v) => setDay(day, (cur) => ({ ...cur, ranges: cur.ranges.map((x, j) => (j === i ? { ...x, close: v } : x)) }))} />
                          {d.ranges.length > 1 && (
                            <button
                              type="button"
                              aria-label={`Remove ${DAY_LABELS[day]} time block ${i + 1}`}
                              onClick={() => setDay(day, (cur) => ({ ...cur, ranges: cur.ranges.filter((_, j) => j !== i) }))}
                              className="inline-flex h-8 w-8 items-center justify-center text-dim hover:text-danger"
                            >
                              <X size={12} />
                            </button>
                          )}
                          {i === d.ranges.length - 1 && (
                            <button
                              type="button"
                              onClick={() => setDay(day, (cur) => ({ ...cur, ranges: [...cur.ranges, { open: '17:00', close: '22:00' }] }))}
                              className="inline-flex min-h-8 items-center gap-1 text-[0.6875rem] uppercase tracking-eyebrow text-dim hover:text-ink"
                            >
                              <Plus size={11} /> Add time block
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
          <p className="text-small text-dim">{scheduleText(active)}</p>
        </div>

        {!readOnly && addRow}
      </div>
    </Collapsible>
  )
}

const CUSTOM = 'custom'
const PRESET_OPTIONS = [...SCHEDULE_PRESETS.map((p) => ({ value: p.id as string, label: p.label })), { value: CUSTOM, label: 'Custom name…' }]
const TIME_SELECT_OPTIONS = TIME_OPTIONS.map((t) => ({ value: t, label: to12h(t) }))

type AddRowProps = { presetId: string; setPresetId: (v: string) => void; customName: string; setCustomName: (v: string) => void; onAdd: () => void }

function AddRow({ presetId, setPresetId, customName, setCustomName, onAdd }: AddRowProps) {
  const custom = presetId === CUSTOM
  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label="Add a schedule" className="min-w-44">
        <Select value={presetId} onValueChange={setPresetId} options={PRESET_OPTIONS} />
      </Field>
      {custom && (
        <Field label="New schedule name" className="w-56">
          <Input
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder="e.g. Patio, Holiday hours"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAdd())}
          />
        </Field>
      )}
      <Button variant="ghost" size="sm" arrow={false} icon={<Plus size={12} />} onClick={onAdd} disabled={custom && !customName.trim()}>
        Add
      </Button>
    </div>
  )
}

export function TimeSelect({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <Select aria-label={label} value={value} onValueChange={onChange} options={TIME_SELECT_OPTIONS} size="sm" className="w-[6.5rem] sm:w-28" />
  )
}
